// @flow
import React from 'react';

import moment from 'moment';

const formatDate = timestamp => (timestamp ? moment(timestamp).format('DD-MM-YYYY') : '');

function renderNoResults(header) {
  return <div>
    <h1>{header}</h1>
    <h3>No Results</h3>
  </div>;
}

export const renderTotalVisits = (totalMonthVisits, totalYearVisits) => (
  <div>
    <h1>Total Visits</h1>
    <table>
      <tr>
        <td>Total Visits This Month</td>
        <td>{totalMonthVisits}</td>
      </tr>
      <tr>
        <td>Total Visits This Year</td>
        <td>{totalYearVisits}</td>
      </tr>
    </table>
  </div>
);

export const renderLabs = (labs, fbcCount, lftCount) => {
  if (!labs.length) {
    return renderNoResults('Labs');
  }
  const labsByDate = new Map();

  // labsByDate = {date: [{name, labName, date}]}
  labs.forEach((item) => {
    labsByDate.set(item.date, [...(labsByDate.get(item.date) || []), item]);
  });

  // labsByDate = {date: [{name: [labName]}]}
  labsByDate.forEach((items, date) => {
    const labsByName = new Map();
    items.forEach((item) => {
      labsByName.set(item.name, [...(labsByName.get(item.name) || []), item.labName]);
    });
    labsByDate.set(date, labsByName);
  });

  const labTableRows = [...labsByDate.entries()].sort().map(([date, labsByNameArray]) => (
    [...labsByNameArray.entries()].sort().map(([name, labNames]) => (
      <tr key={labNames}>
        <td>{moment(date).format()}</td>
        <td>{name}</td>
        <td>{labNames.join(', ')}</td>
      </tr>
    ))));

  return (<div>
    <h1>Labs</h1>
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Name</th>
          <th>Labs</th>
        </tr>
      </thead>
      <tbody>
        {labTableRows}
      </tbody>
    </table>
    <br />
    FBC Count: {fbcCount}
    <br />
    LFT Count: {lftCount}
  </div>);
};

export const renderImaging = (imaging) => {
  if (!imaging.length) {
    return renderNoResults('Imaging');
  }

  const imagingByDate = new Map();

  // imagingByDate = {date: [{name, documentName, date}]}
  imaging.forEach((item) => {
    imagingByDate.set(item.date, [...(imagingByDate.get(item.date) || []), item]);
  });

  // imagingByDate = {date: [{name: [documentName]}]}
  imagingByDate.forEach((items, date) => {
    const docsByName = new Map();
    items.forEach((item) => {
      docsByName.set(item.name, [...(docsByName.get(item.name) || []), item.documentName]);
    });
    imagingByDate.set(date, docsByName);
  });

  const imagingTableRows = [...imagingByDate.entries()].sort().map(([date, docsByNameArray]) => (
    [...docsByNameArray.entries()].sort().map(([name, documentNames]) => (
      <tr key={documentNames}>
        <td>{moment(date).format()}</td>
        <td>{name}</td>
        <td>{documentNames.join(', ')}</td>
      </tr>
    ))));

  return (<div>
    <h1>Imaging</h1>
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Name</th>
          <th>Documents</th>
        </tr>
      </thead>
      <tbody>
        {imagingTableRows}
      </tbody>
    </table></div>);
};

export const renderSpecialistAppointments = (specialistAppointments) => {
  if (!specialistAppointments.length) {
    return renderNoResults('Specialist Appointments');
  }

  const apptByDate = new Map();

  // apptByDate = {date: [{name, doctorName, location, documentName, date}]}
  specialistAppointments.forEach((item) => {
    apptByDate.set(item.date, [...(apptByDate.get(item.date) || []), item]);
  });

  // apptByDate = {date: [{name: [name, doctorName, location, documentName, date]}]}
  apptByDate.forEach((items, date) => {
    const apptsByName = new Map();
    items.forEach((item) => {
      apptsByName.set(item.name, [...(apptsByName.get(item.name) || []), item]);
    });
    apptByDate.set(date, apptsByName);
  });

  const apptTableRows = [...apptByDate.entries()].sort().map(([date, apptsByNameArray]) => (
    [...apptsByNameArray.entries()].sort().map(([name, appts]) => (
      <tr key={name}>
        <td>{moment(date).format()}</td>
        <td>{name}</td>
        <td>{appts.map(appt => `${appt.doctorName} @ ${appt.location} (${appt.documentName})`).join('\n')}</td>
      </tr>
    ))));

  return (<div>
    <h1>Specialist Appointments</h1>
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Name</th>
          <th>Appointments - Doctor Name @ Location (Document Name)</th>
        </tr>
      </thead>
      <tbody>
        {apptTableRows}
      </tbody>
    </table></div>);
};

export const renderSurgeries = (surgeries) => {
  if (!surgeries.length) {
    return renderNoResults('Surgeries');
  }

  surgeries.sort((a, b) => moment(a.date).unix() - moment(b.date).unix());
  const count = surgeries.length;
  const surgeriesRows = surgeries.map(surgery => (
    <tr key={surgery.doctorName}>
      <td>{formatDate(surgery.date)}</td>
      <td>{surgery.doctorName}</td>
      <td>{surgery.location}</td>
      <td>{surgery.documentName}</td>
    </tr>
  ));
  return (
    <div>
      <h1>Surgeries: {count}</h1>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Doctor Name</th>
            <th>Location</th>
            <th>Document Name</th>
          </tr>
        </thead>
        <tbody>
          {surgeriesRows}
        </tbody>
      </table>
    </div>
  );
};

export const renderNewBabies = (newBabies) => {
  if (!newBabies.length) {
    return renderNoResults('New Babies');
  }

  newBabies.sort((a, b) => moment(a.dateArrived).unix() - moment(b.dateArrived).unix());
  const count = newBabies.length;
  const newBabiesRows = newBabies.map(baby => (
    <tr key={baby.name}>
      <td>{formatDate(baby.dateArrived)}</td>
      <td>{baby.name}</td>
      <td>{formatDate(baby.dob)}</td>
      <td>{baby.admissionHivTestResult}</td>
      <td>{baby.admissionHbTestResult}</td>
    </tr>
  ));
  return (
    <div>
      <h1>New Babies: {count}</h1>
      <table>
        <thead>
          <tr>
            <th>Date Arrived</th>
            <th>Name</th>
            <th>Date of Birth</th>
            <th>Admission HIV Test Result</th>
            <th>Admission HB Test Result</th>
          </tr>
        </thead>
        <tbody>
          {newBabiesRows}
        </tbody>
      </table>
    </div>
  );
};

export const renderHospitalAdmissions = (hospitalAdmissions) => {
  if (!hospitalAdmissions.length) {
    return renderNoResults('Hospital Admissions');
  }

  hospitalAdmissions.sort((a, b) => moment(a.admissionDate).unix() - moment(b.admissionDate).unix());
  const count = hospitalAdmissions.length;
  const hospitalAdmissionRows = hospitalAdmissions.map(hospitalAdmission => (
    <tr key={hospitalAdmission.name}>
      <td>{hospitalAdmission.name}</td>
      <td>{formatDate(hospitalAdmission.admissionDate)}</td>
      <td>{formatDate(hospitalAdmission.dischargeDate)}</td>
      <td>{hospitalAdmission.location}</td>
      <td>{hospitalAdmission.diagnosis}</td>
    </tr>
  ));
  return (
    <div>
      <h1>Hospital Admissions: {count}</h1>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>From</th>
            <th>To</th>
            <th>Location</th>
            <th>Diagnosis</th>
          </tr>
        </thead>
        <tbody>
          {hospitalAdmissionRows}
        </tbody>
      </table>
    </div>
  );
};

export const renderDietChanges = (diets) => {
  if (!diets.length) {
    return renderNoResults('Dietary');
  }

  diets.sort((a, b) => a.name.localeCompare(b.name));
  const count = diets.length;
  const dietRows = diets.map(diet => (
    <tr key={diet.name}>
      <td>{diet.name}</td>
      <td>{formatDate(diet.from)}</td>
      <td>{formatDate(diet.to)}</td>
      <td>{diet.extraSnack ? '✓' : ''}</td>
      <td>{diet.highCalDiet ? '✓' : ''}</td>
      <td>{diet.notes}</td>
    </tr>
  ));
  return (
    <div>
      <h1>Dietary: {count}</h1>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>From</th>
            <th>To</th>
            <th>Extra Snack</th>
            <th>Jonathan diet</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {dietRows}
        </tbody>
      </table>
    </div>
  );
};

export const renderDiagnoses = (acuteOrChronic, diagnoses) => {
  if (!diagnoses.length) {
    return renderNoResults(`${acuteOrChronic} Diagnoses`);
  }

  let diagnosesGrouped = new Map();
  diagnoses.forEach((entry) => {
    const { name, diagnosis } = entry;
    const diagnosisNonNull = diagnosis || '';
    if (!diagnosesGrouped.has(diagnosisNonNull)) { diagnosesGrouped.set(diagnosisNonNull, []); }
    diagnosesGrouped.get(diagnosisNonNull).push(name);
  });
  [...diagnosesGrouped.values()].forEach(names => names.sort());
  diagnosesGrouped = [...diagnosesGrouped.entries()]
    // Sort by # affected, then alphabetical by diagnosis name.
    .sort(([diagnosisA, namesA], [diagnosisB, namesB]) => namesB.length - namesA.length || diagnosisA.localeCompare(diagnosisB))
    .map(([diagnosis, names]) => {
      const count = names.length;
      return (
        <tr key={diagnosis}>
          <td>{diagnosis}</td>
          <td>{count}</td>
          <td>{names.join(', ')}</td>
        </tr>
      );
    });

  return (
    <div>
      <h1>{acuteOrChronic} Diagnoses</h1>
      <table>
        <thead>
          <tr>
            <th>{acuteOrChronic} Diagnoses/Sickness</th>
            <th># Affected this month</th>
            <th>Specific Kids</th>
          </tr>
        </thead>
        <tbody>
          {diagnosesGrouped}
        </tbody>
      </table>
    </div>
  );
};



// WEBPACK FOOTER //
// ./client/src/components/monthlyReportPages/monthlyReportUtil.js